// Admin Panel - Tasks Page
// File: admin-panel/src/pages/Tasks.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, onSnapshot, updateDoc, doc, orderBy, where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../services/firebase';
import {
  TruckIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PhoneIcon,
  ClockIcon,
  EyeIcon,
  UserIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'picked_up', label: 'Picked Up' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'failed', label: 'Failed' },
];

const PAYMENT_OPTIONS = [
  { value: 'all', label: 'All Payments' },
  { value: 'cash', label: 'Cash' },
  { value: 'online', label: 'Online' },
  { value: 'prepaid', label: 'Prepaid' },
];

function TaskCard({ task, porters, onAssign }) {
  const [assigning, setAssigning] = useState(false);
  const [selectedPorter, setSelectedPorter] = useState(task.porterId || '');

  const getStatusColor = (status) => {
    const colors = {
      unassigned: 'bg-gray-100 text-gray-700 border-gray-300',
      pending: 'bg-yellow-50 text-yellow-700 border-yellow-300',
      accepted: 'bg-blue-50 text-blue-700 border-blue-300',
      picked_up: 'bg-purple-50 text-purple-700 border-purple-300',
      in_transit: 'bg-indigo-50 text-indigo-700 border-indigo-300',
      delivered: 'bg-green-100 text-green-700 border-green-300',
      failed: 'bg-red-100 text-red-700 border-red-300',
    };
    return colors[status] || colors.pending;
  };

  const getPaymentColor = (mode) => {
    const colors = {
      cash: 'bg-yellow-50 text-yellow-700',
      online: 'bg-blue-50 text-blue-700',
      prepaid: 'bg-green-100 text-green-700',
    };
    return colors[mode] || 'bg-gray-100 text-gray-600';
  };

  const getPriorityBadge = (priority) => {
    if (priority === 'urgent') return <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">🔴 URGENT</span>;
    if (priority === 'high') return <span className="px-2 py-1 bg-orange-500 text-white text-xs font-bold rounded">🟠 HIGH</span>;
    return null;
  };

  const handleAssign = async () => {
    if (!selectedPorter) return;
    setAssigning(true);
    try {
      const porter = porters.find(p => p.id === selectedPorter);
      await onAssign(task.id, selectedPorter, porter?.name);
    } finally {
      setAssigning(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const productCount = task.package?.products?.length || 0;
  const itemCount = task.package?.itemCount || task.package?.products?.reduce((sum, p) => sum + (p.quantity || 1), 0) || 0;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-3">
          <span className="text-red-600 font-bold text-lg">{task.invoiceNumber}</span>
          {getPriorityBadge(task.priority)}
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-lg text-xs font-medium border ${getStatusColor(task.status)}`}>
            {task.status?.replace('_', ' ').toUpperCase()}
          </span>
          <span className={`px-3 py-1 rounded-lg text-xs font-medium ${getPaymentColor(task.payment?.mode)}`}>
            {task.payment?.mode === 'cash' && '💵'}
            {task.payment?.mode === 'online' && '💳'}
            {task.payment?.mode === 'prepaid' && '✅'}
            {' '}{task.payment?.mode?.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {/* Customer */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center text-white font-bold">
            {task.customer?.name?.charAt(0) || 'C'}
          </div>
          <div className="flex-1">
            <p className="text-gray-900 font-semibold">{task.customer?.name}</p>
            <p className="text-gray-500 text-sm">{task.customer?.phone}</p>
          </div>
          {task.customer?.phone && (
            <a
              href={`tel:${task.customer.phone}`}
              className="p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors"
            >
              <PhoneIcon className="w-5 h-5" />
            </a>
          )}
        </div>

        {/* Products Summary */}
        {productCount > 0 && (
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
            <CubeIcon className="w-5 h-5 text-gray-500" />
            <span className="text-gray-700 text-sm">
              {productCount} product{productCount > 1 ? 's' : ''} • {itemCount} item{itemCount > 1 ? 's' : ''}
            </span>
          </div>
        )}

        {/* Locations */}
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <div className="w-2 h-2 bg-green-600 rounded-full"></div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-gray-400 text-xs font-medium">PICKUP</p>
              <p className="text-gray-900 text-sm truncate">{task.pickup?.address}</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <div className="w-2 h-2 bg-red-600 rounded-full"></div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-gray-400 text-xs font-medium">DELIVERY</p>
              <p className="text-gray-900 text-sm truncate">{task.delivery?.address}</p>
            </div>
          </div>
        </div>

        {/* Amount */}
        <div className="flex items-center justify-between py-3 px-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl">
          <span className="text-gray-600 font-medium">Amount to Collect</span>
          <span className="text-2xl font-bold text-gray-900">₹{task.payment?.amount?.toLocaleString('en-IN')}</span>
        </div>

        {/* Porter Assignment */}
        {task.status === 'unassigned' || !task.porterId ? (
          <div className="flex items-center gap-2">
            <select
              value={selectedPorter}
              onChange={(e) => setSelectedPorter(e.target.value)}
              className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="">Select Porter</option>
              {porters.filter(p => p.isOnline).map(porter => (
                <option key={porter.id} value={porter.id}>🟢 {porter.name}</option>
              ))}
              {porters.filter(p => !p.isOnline).map(porter => (
                <option key={porter.id} value={porter.id}>⚫ {porter.name}</option>
              ))}
            </select>
            <button
              onClick={handleAssign}
              disabled={!selectedPorter || assigning}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 shadow-lg shadow-red-600/20"
            >
              {assigning ? '...' : 'Assign'}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between py-2 px-3 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <UserIcon className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-blue-700 text-sm font-medium">{task.porterName || 'Assigned'}</p>
                <p className="text-blue-500 text-xs">Porter</p>
              </div>
            </div>
            {task.status !== 'delivered' && task.status !== 'failed' && (
              <button
                onClick={() => onAssign(task.id, null, null)}
                className="text-gray-500 hover:text-red-600 text-xs font-medium px-2 py-1 hover:bg-red-50 rounded transition-colors"
              >
                Unassign
              </button>
            )}
          </div>
        )}

        {/* Meta */}
        <div className="flex items-center justify-between text-sm text-gray-500 pt-2 border-t border-gray-100">
          <span className="flex items-center gap-1">
            <ClockIcon className="w-4 h-4" />
            {formatDate(task.createdAt)}
          </span>
          <span className="flex items-center gap-1">
            ⏱️ {task.estimatedTime}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-100 bg-gray-50">
        <Link
          to={`/spares-admin/tasks/${task.id}`}
          className="w-full flex items-center justify-center gap-2 py-3 bg-white hover:bg-gray-100 text-gray-700 text-sm font-medium rounded-xl transition-colors border border-gray-200"
        >
          <EyeIcon className="w-4 h-4" />
          View Details
        </Link>
      </div>
    </div>
  );
}

function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [porters, setPorters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [currentUser, setCurrentUser] = useState(null);

  // Get current authenticated admin user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Load tasks for current vendor (Main Admin)
  useEffect(() => {
    if (!currentUser) return;

    // Query tasks created by this Main Admin
    const tasksQuery = query(
      collection(db, 'tasks'),
      where('vendorId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeTasks = onSnapshot(tasksQuery, (snapshot) => {
      const tasksList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTasks(tasksList);
      setLoading(false);
    }, (error) => {
      console.error('Tasks query error:', error);
      // Fallback: try without orderBy if index not created
      const fallbackQuery = query(
        collection(db, 'tasks'),
        where('vendorId', '==', currentUser.uid)
      );
      onSnapshot(fallbackQuery, (snapshot) => {
        const tasksList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Sort manually
        tasksList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setTasks(tasksList);
        setLoading(false);
      });
    });

    return () => unsubscribeTasks();
  }, [currentUser]);

  // Load porters
  useEffect(() => {
    const portersQuery = query(collection(db, 'porters'), where('isActive', '==', true));
    const unsubscribePorters = onSnapshot(portersQuery, (snapshot) => {
      const portersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPorters(portersList);
    });

    return () => unsubscribePorters();
  }, []);

  const handleAssignPorter = async (taskId, porterId, porterName) => {
    try {
      await updateDoc(doc(db, 'tasks', taskId), {
        porterId: porterId,
        porterName: porterName,
        status: porterId ? 'pending' : 'unassigned',
        updatedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Assign porter error:', error);
      alert('Failed to assign porter');
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = 
      task.invoiceNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.customer?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.customer?.phone?.includes(searchQuery);
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesPayment = paymentFilter === 'all' || task.payment?.mode === paymentFilter;
    return matchesSearch && matchesStatus && matchesPayment;
  });

  // Calculate stats
  const stats = {
    total: tasks.length,
    unassigned: tasks.filter(t => t.status === 'unassigned').length,
    pending: tasks.filter(t => t.status === 'pending').length,
    inProgress: tasks.filter(t => ['accepted', 'picked_up', 'in_transit'].includes(t.status)).length,
    delivered: tasks.filter(t => t.status === 'delivered').length,
    failed: tasks.filter(t => t.status === 'failed').length,
  };

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
          <p className="text-gray-500 font-medium">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent"></div>
          <p className="text-gray-500 font-medium">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Delivery Tasks</h1>
          <p className="text-gray-500 mt-1">Manage and track all your deliveries</p>
        </div>
        <Link
          to="/spares-admin/create-task"
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-xl shadow-lg shadow-red-600/30 transition-all"
        >
          <TruckIcon className="w-5 h-5" />
          Create Task
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <p className="text-gray-500 text-sm">Total</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <p className="text-gray-500 text-sm">Unassigned</p>
          <p className="text-2xl font-bold text-gray-600">{stats.unassigned}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-yellow-100">
          <p className="text-yellow-600 text-sm">Pending</p>
          <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-blue-100">
          <p className="text-blue-600 text-sm">In Progress</p>
          <p className="text-2xl font-bold text-blue-700">{stats.inProgress}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-green-100">
          <p className="text-green-600 text-sm">Delivered</p>
          <p className="text-2xl font-bold text-green-700">{stats.delivered}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-red-100">
          <p className="text-red-600 text-sm">Failed</p>
          <p className="text-2xl font-bold text-red-700">{stats.failed}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by invoice, customer name or phone..."
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent min-w-[150px]"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
            className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent min-w-[150px]"
          >
            {PAYMENT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Quick Status Filters */}
        <div className="flex flex-wrap gap-2 mt-4">
          {STATUS_OPTIONS.slice(1).map(opt => {
            const count = tasks.filter(t => t.status === opt.value).length;
            return (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(statusFilter === opt.value ? 'all' : opt.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  statusFilter === opt.value 
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/30' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {opt.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Tasks Grid */}
      {filteredTasks.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredTasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              porters={porters}
              onAssign={handleAssignPorter}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center shadow-sm">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <TruckIcon className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Tasks Found</h3>
          <p className="text-gray-500 mb-6">
            {searchQuery || statusFilter !== 'all' || paymentFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Create your first delivery task to get started'}
          </p>
          <Link
            to="/spares-admin/create-task"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-red-600/30"
          >
            <TruckIcon className="w-5 h-5" />
            Create Your First Task
          </Link>
        </div>
      )}
    </div>
  );
}

export default Tasks;